import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { Currency, Quote, QuoteParcel, QuoteParcels, QuoteRequirementSection } from '~/lib/types/basket-types';
import Parcel from './parcel';
import { P2GButton, P2GFade, P2GGrid, P2GIcon, SvgAdd } from '@parcel2go/core-ui';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { areDimensionsOptionalForQuote } from '~/lib/quotes';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import Section from '~/components/core/section/section';
import ParcelTotal from '~/components/core/parcels/parcel-total';
import { SumNumber } from '~/lib/utils/array';

type ParcelsProps = {
  section: IP2GBasketSection<QuoteParcels>;
  quote: Quote;
  errors: Record<string, string>;
  requirements: QuoteRequirementSection;
  currency: Currency;
};

export default function Parcels({ section, quote, errors, requirements, currency }: ParcelsProps) {
  const { data, update, loading } = section;
  const { track } = useAnalytics();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [requiresDimensions, setRequiresDimensions] = useState(false);
  const [newItems, setNewItems] = useState<string[]>([]);

  const parcels: QuoteParcel[] = useMemo(() => {
    return data && data.parcels && Array.isArray(data.parcels) ? data.parcels : [];
  }, [data]);

  useEffect(() => {
    setRequiresDimensions(
      (currentValue) =>
        currentValue || !areDimensionsOptionalForQuote(quote) || parcels.some((p) => p.length || p.width || p.height)
    );
  }, [quote, parcels]);

  const onAdd = useCallback(() => {
    track({ type: 'checkout_action', payload: { action: 'add_parcel' } });
    const id = uuidv4();
    update({
      ...data,
      parcels: [
        ...parcels,
        {
          id: id,
          weight: parcels.length > 0 ? parcels[0].weight : 0,
          length: null,
          width: null,
          height: null,
          value: null,
          contentSummary: '',
          quantity: 1,
          contents: []
        }
      ]
    });

    setNewItems((x) => [...x, id]);
  }, [track, update, data, parcels]);

  const onRemove = useCallback(
    (parcel: QuoteParcel) => {
      track({ type: 'checkout_action', payload: { action: 'remove_parcel' } });
      update({ ...data, parcels: data.parcels.filter((p: QuoteParcel) => p.id !== parcel.id) });
    },
    [data, track, update]
  );

  const onUpdate = useCallback(
    (parcel: QuoteParcel) => {
      update({ ...data, parcels: data.parcels.map((p: QuoteParcel) => (p.id === parcel.id ? parcel : p)) });
    },
    [data, update]
  );

  const onDuplicate = useCallback(
    (parcel: QuoteParcel) => {
      track({ type: 'checkout_action', payload: { action: 'duplicate_parcel' } });
      const id = uuidv4();
      const newParcels = [...data.parcels];
      newParcels.splice(newParcels.indexOf(parcel) + 1, 0, { ...parcel, id: id });

      update({ ...data, parcels: newParcels });

      setNewItems((x) => [...x, id]);
    },
    [data, track, update]
  );

  return (
    <P2GGrid container spacing={2} dataTestId="parcel-collection" direction="column">
      {parcels.map((parcel, index) => (
        <P2GGrid item key={parcel.id}>
          <P2GFade in timeout={newItems.includes(parcel.id) ? 1000 : 300}>
            <Section
              title={
                parcels.length > 1
                  ? t('PARCEL_TITLE_NUMBER', { defaultValue: `Parcel {{number}}`, number: index + 1 })
                  : t('PARCEL_TITLE_SINGLE', { defaultValue: 'Parcel Details' })
              }
              dataTestId={`parcels-section-${index}`}
              loading={section.loading || !quote || loading}
            >
              <Parcel
                index={index}
                loading={loading}
                parcel={parcel}
                onChange={onUpdate}
                onRemove={onRemove}
                onDuplicate={onDuplicate}
                restrictions={quote.restrictions}
                errors={errors}
                removeable={parcels.length > 1}
                requirements={requirements}
                currency={currency}
                onExpand={() => setRequiresDimensions(true)}
                requiresDimensions={requiresDimensions}
              />
            </Section>
          </P2GFade>
        </P2GGrid>
      ))}
      {onAdd !== undefined && (
        <P2GGrid item>
          <P2GButton
            size="large"
            variant="tertiary"
            fullWidth
            onClick={onAdd}
            dataTestId="btn-add-parcel"
            key="btn-add-parcel"
            startIcon={
              <P2GIcon>
                <SvgAdd />
              </P2GIcon>
            }
          >
            {t('PARCEL_ADD_BUTTON', {
              defaultValue: 'Add Another Parcel'
            })}
          </P2GButton>
        </P2GGrid>
      )}

      {parcels.length > 1 && (
        <P2GGrid item xs={12}>
          <ParcelTotal currency={currency} value={SumNumber(parcels.map((x) => x.value || 0))} count={parcels.length} />
        </P2GGrid>
      )}
    </P2GGrid>
  );
}
